import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';

export interface LegalBackLink {
  href: string;
  label: string;
}

interface LegalPageLayoutProps {
  title: string;
  lastUpdated: string;
  icon: React.ReactNode;
  iconGradientClassName: string;
  cardTitle: string;
  defaultBack: LegalBackLink;
  settingsBack?: LegalBackLink;
  footer?: LegalBackLink & { buttonGradientClassName: string };
  maxWidthClassName?: string;
  children: React.ReactNode;
}

const LegalPageLayout: React.FC<LegalPageLayoutProps> = ({
  title,
  lastUpdated,
  icon,
  iconGradientClassName,
  cardTitle,
  defaultBack,
  settingsBack = { href: '/settings', label: 'Back to Settings' },
  footer,
  maxWidthClassName = 'max-w-4xl',
  children,
}) => {
  const [searchParams] = useSearchParams();
  const fromSettings = searchParams.get('from') === 'settings';
  const backLink = fromSettings ? settingsBack : defaultBack;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-gray-950 dark:via-slate-900 dark:to-indigo-950 py-12 px-4 sm:px-6 lg:px-8">
      <div className={`${maxWidthClassName} mx-auto`}>
        <div className="mb-8">
          <Link
            to={backLink.href}
            className="inline-flex items-center text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300 transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {backLink.label}
          </Link>

          <div className="flex items-center space-x-3 mb-4">
            <div
              className={`w-12 h-12 bg-gradient-to-r ${iconGradientClassName} rounded-xl flex items-center justify-center shadow-lg`}
            >
              {icon}
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">{title}</h1>
              <p className="text-gray-600 dark:text-gray-400">Last updated: {lastUpdated}</p>
            </div>
          </div>

          {fromSettings && (
            <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
              <ExternalLink className="h-3.5 w-3.5" />
              Opened from Settings — you can close this tab when finished.
            </p>
          )}
        </div>

        <Card className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm border-white/20 dark:border-gray-700/50 shadow-xl">
          <CardHeader>
            <CardTitle className="text-xl text-gray-900 dark:text-gray-100">{cardTitle}</CardTitle>
          </CardHeader>
          <CardContent className="max-w-none">{children}</CardContent>
        </Card>

        {footer && (
          <div className="mt-8 text-center">
            <Link to={fromSettings ? settingsBack.href : footer.href}>
              <Button
                className={`bg-gradient-to-r ${footer.buttonGradientClassName} text-white`}
              >
                {fromSettings ? settingsBack.label : footer.label}
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default LegalPageLayout;
